import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUniversalLibrary } from "@/contexts/UniversalLibraryContext";
import { LibraryItem } from "@/contexts/UniversalLibraryContext/types";
import { fetchUniversalStorageItems } from "./universal-storage-operations";

// Simple debounce hook implementation
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function useUniversalLibraryData() {
  const {
    selectedContentType,
    searchQuery,
    selectedCategory,
    contentTypes,
    onRefetchNeeded,
  } = useUniversalLibrary();

  // STABILIZE ADAPTERS with useMemo - only recreate when contentTypes actually changes
  const adapters = useMemo(() =>
    Object.entries(contentTypes).reduce((map, [type, config]) => {
      map[type] = config.adapter;
      return map;
    }, {} as Record<string, any>),
    [contentTypes]
  );

  // DEBOUNCE search query to prevent excessive API calls during typing
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  // React Query for data fetching with caching - normalize undefined to null for consistent caching
  const queryKey = ['universal-storage-items', selectedContentType, selectedCategory || null, debouncedSearchQuery || ''];
  
  const {
    data: items = [],
    isLoading,
    error,
    refetch: refetchQuery
  } = useQuery({
    queryKey,
    queryFn: async () => {
      console.log('[UniversalLibraryData] React Query fetching with params:', {
        selectedContentType,
        selectedCategory,
        debouncedSearchQuery,
        availableAdapters: Object.keys(adapters),
        timestamp: new Date().toISOString()
      });

      // Prepare content type filter(s)
      const contentTypeFilter =
        selectedContentType && selectedContentType !== "all"
          ? [selectedContentType]
          : undefined;

      console.log('[UniversalLibraryData] Calling fetchUniversalStorageItems with filter:', {
        contentTypes: contentTypeFilter,
        categoryId: selectedCategory
      });

      // Direct database call - normalize selectedCategory to prevent duplicate calls
      const libraryEntries = await fetchUniversalStorageItems({
        contentTypes: contentTypeFilter,
        categoryId: selectedCategory || null,
      });

      console.log('[UniversalLibraryData] Received library entries:', {
        count: libraryEntries.length,
        entries: libraryEntries,
        timestamp: new Date().toISOString()
      });

      // Process entries with stable adapters
      const adaptedItems: LibraryItem[] = libraryEntries
        .map((item, index) => {
          const type = item.content_type;
          console.log(`[UniversalLibraryData] Processing item ${index + 1}/${libraryEntries.length}:`, {
            storageId: item.id,
            contentType: type,
            workbook_image_id: item.workbook_image_id,
            workbook_research_id: item.workbook_research_id,
            workbook_page_id: item.workbook_page_id,
            hasAdapter: !!adapters[type]
          });

          // Get data based on content type
          let data = undefined;
          if (type === "image" && item.image_data) {
            data = item.image_data;
            console.log(`[UniversalLibraryData] Using image_data for item ${item.id}:`, data);
          } else if (type === "research" && item.research_data) {
            data = item.research_data;
            console.log(`[UniversalLibraryData] Using research_data for item ${item.id}:`, data);
          } else if (type === "workbook" && item.workbook_data) {
            data = item.workbook_data;
            console.log(`[UniversalLibraryData] Using workbook_data for item ${item.id}:`, data);
          } else if (type === "private_image" && item.private_image_data) {
            data = item.private_image_data;
            console.log(`[UniversalLibraryData] Using private_image_data for item ${item.id}:`, data);
          } else if (type === "private_research" && item.private_research_data) {
            data = item.private_research_data;
            console.log(`[UniversalLibraryData] Using private_research_data for item ${item.id}:`, data);
          } else if (type === "private_page" && item.private_page_data) {
            data = item.private_page_data;
            console.log(`[UniversalLibraryData] Using private_page_data for item ${item.id}:`, data);
          }
          // 🎯 NEW CONTENT TYPE DATA HANDLING CAN BE ADDED HERE
          // Example: else if (type === "new_type" && item.new_type_data) {
          //   data = item.new_type_data;
          // }
          
          if (!adapters[type]) {
            console.warn(`[UniversalLibraryData] No adapter found for content type '${type}'. Available adapters:`, Object.keys(adapters));
            return null;
          }

          if (!data) {
            console.warn(`[UniversalLibraryData] No data found for item ${item.id} (type: ${type}). Available data fields:`, {
              image_data: !!item.image_data,
              research_data: !!item.research_data,
              workbook_data: !!item.workbook_data,
              private_image_data: !!item.private_image_data,
              private_research_data: !!item.private_research_data,
              private_page_data: !!item.private_page_data
            });
            return null;
          }
          
          // Get the adapted item from the content-specific adapter
          try {
            const adaptedItem = adapters[type].adapt(data);
            console.log(`[UniversalLibraryData] Successfully adapted item ${item.id}:`, {
              originalType: type,
              adaptedTitle: adaptedItem.title,
              adaptedContentType: adaptedItem.contentType,
              hasImageUrl: !!adaptedItem.imageUrl
            });

            // Include universal storage metadata
            const finalItem = {
              ...adaptedItem,
              id: item.id,
              originalItem: {
                ...item, // Include the full universal storage item (with display_order!)
                ...data,  // Include the content data
                display_order: item.display_order !== null && item.display_order !== undefined ? item.display_order : 1000
              }
            };

            return finalItem;
          } catch (adapterError) {
            console.error(`[UniversalLibraryData] Adapter error for item ${item.id} (type: ${type}):`, adapterError);
            return null;
          }
        })
        .filter(Boolean);

      console.log('[UniversalLibraryData] Adapted items before search filter:', {
        count: adaptedItems.length,
        items: adaptedItems.map(item => ({
          id: item.id,
          title: item.title,
          contentType: item.contentType
        })),
        timestamp: new Date().toISOString()
      });

      // Apply keyword search on title/description (case-insensitive)
      const filteredItems = debouncedSearchQuery ? adaptedItems.filter(
        (item) => {
          const titleMatch = item.title?.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
          const descriptionMatch = item.description?.toLowerCase().includes(debouncedSearchQuery.toLowerCase());
          const matches = titleMatch || descriptionMatch;
          
          if (matches) {
            console.log(`[UniversalLibraryData] Item ${item.id} matches search "${debouncedSearchQuery}":`, {
              title: item.title,
              titleMatch,
              descriptionMatch
            });
          }
          
          return matches;
        }
      ) : adaptedItems;

      console.log('[UniversalLibraryData] Final filtered items:', {
        count: filteredItems.length,
        searchQuery: debouncedSearchQuery,
        items: filteredItems.map(item => ({
          id: item.id,
          title: item.title,
          contentType: item.contentType
        })),
        timestamp: new Date().toISOString()
      });

      return filteredItems;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
    refetchOnWindowFocus: false,
    enabled: Object.keys(adapters).length > 0, // Only run when adapters are ready
  });

  // Register the refetch function with the context for external cache invalidation
  useEffect(() => {
    console.log('[UniversalLibraryData] Registering refetch callback with context');
    const cleanup = onRefetchNeeded(refetchQuery);
    console.log('[UniversalLibraryData] Refetch callback registered, cleanup function created');
    
    return () => {
      console.log('[UniversalLibraryData] Component unmounting, cleaning up refetch callback');
      cleanup();
    };
  }, [onRefetchNeeded]); // Removed refetchQuery from dependencies to prevent infinite loop

  return { 
    items, 
    isLoading, 
    error,
    refetch: refetchQuery // Expose React Query refetch for manual updates
  };
}

// COUNTS HOOK with React Query for caching
export function useUniversalLibraryCounts() {
  const { contentTypes } = useUniversalLibrary();

  // STABILIZE ADAPTERS for counts
  const adapters = useMemo(() =>
    Object.entries(contentTypes).reduce((map, [type, config]) => {
      map[type] = config.adapter;
      return map;
    }, {} as Record<string, any>),
    [contentTypes]
  );

  // React Query for counts with caching
  const {
    data: counts = {
      total: 0,
      image: 0,
      research: 0,
      workbook: 0,
      private_image: 0,
      private_research: 0,
      categories: {} as Record<string, number>,
    },
    isLoading,
    error
  } = useQuery({
    queryKey: ['universal-storage-counts'],
    queryFn: async () => {
      console.log('[UniversalLibraryCounts] React Query fetching counts:', {
        availableAdapters: Object.keys(adapters),
        timestamp: new Date().toISOString()
      });

      // Get all items without filters to calculate counts
      const allLibraryEntries = await fetchUniversalStorageItems({});

      console.log('[UniversalLibraryCounts] Received library entries for counts:', {
        count: allLibraryEntries.length,
        timestamp: new Date().toISOString()
      });

      // Map all entries to get total counts with stable adapters
      const allAdapted: LibraryItem[] = allLibraryEntries
        .map((item) => {
          const type = item.content_type;
          let data = undefined;
          if (type === "image" && item.image_data) {
            data = item.image_data;
          } else if (type === "research" && item.research_data) {
            data = item.research_data;
          } else if (type === "workbook" && item.workbook_data) {
            data = item.workbook_data;
          } else if (type === "private_image" && item.private_image_data) {
            data = item.private_image_data;
          } else if (type === "private_research" && item.private_research_data) {
            data = item.private_research_data;
          } else if (type === "private_page" && item.private_page_data) {
            data = item.private_page_data;
          }
          // 🎯 NEW CONTENT TYPE DATA HANDLING CAN BE ADDED HERE
          // Example: else if (type === "new_type" && item.new_type_data) {
          //   data = item.new_type_data;
          // }
          
          if (!adapters[type] || !data) return null;
          return adapters[type].adapt(data);
        })
        .filter(Boolean);

      // Calculate counts by content type and category
      const totalCount = allAdapted.length;
      const imageCount = allAdapted.filter(item => item.contentType === 'image').length;
      const researchCount = allAdapted.filter(item => item.contentType === 'research').length;
      const workbookCount = allAdapted.filter(item => item.contentType === 'workbook').length;
      const privateImageCount = allAdapted.filter(item => item.contentType === 'private_image').length;
      const privateResearchCount = allAdapted.filter(item => item.contentType === 'private_research').length;
      // 🎯 NEW CONTENT TYPE COUNT CALCULATIONS CAN BE ADDED HERE
      // Example: const newTypeCount = allAdapted.filter(item => item.contentType === 'new_type').length;

      // Calculate category counts from the raw library entries
      const categoryCounts: Record<string, number> = {};
      allLibraryEntries.forEach(item => {
        if (item.category_id) {
          categoryCounts[item.category_id] = (categoryCounts[item.category_id] || 0) + 1;
        }
      });

      const finalCounts = {
        total: totalCount,
        image: imageCount,
        research: researchCount,
        workbook: workbookCount,
        private_image: privateImageCount,
        private_research: privateResearchCount,
        // 🎯 NEW CONTENT TYPE COUNTS CAN BE ADDED HERE
        // Example: new_type: newTypeCount,
        categories: categoryCounts,
      };

      console.log('[UniversalLibraryCounts] Final counts calculated:', {
        counts: finalCounts,
        timestamp: new Date().toISOString()
      });

      return finalCounts;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    enabled: Object.keys(adapters).length > 0, // Only run when adapters are ready
  });

  if (error) {
    console.error('[UniversalLibraryCounts] Query error:', error);
  }

  return { counts, isLoading };
}
